import { AscProjectDeletionErrorSchema } from '@common/neo.tax';
import { Modal, Stack, useModal } from '@ui-components/neo.tax';
import { ReactElement } from 'react';

import { useAscContext } from '@neo.tax/asc/context';
import { useDeleteAscProject, type AscProject } from '@neo.tax/asc/hooks';
import {
  getExtendedErrorsOrDefault,
  getPath,
  Path,
  useCurrentCompanyId,
  useNavigateTo,
  DeleteConfirmationLayout,
} from '@neo.tax/shared';

import { ButtonStyled } from './delete-asc-project.styled';

type DeleteAscProjectProps = {
  ascProject: AscProject;
};

export const DeleteAscProject = ({ ascProject }: DeleteAscProjectProps): ReactElement => {
  const navigate = useNavigateTo();
  const companyId = useCurrentCompanyId();
  const { fiscalYearId, periodName } = useAscContext();
  const { isOpen: isDeleteProjectModalOpen, setIsOpen } = useModal();

  const [deleteAscProject, { loading }] = useDeleteAscProject({
    companyId,
    ascProjectId: ascProject.id,
  });

  const handleOpenModal = (): void => {
    setIsOpen(true);
  };

  const handleCloseModal = (): void => {
    setIsOpen(false);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    try {
      await deleteAscProject();
      // Close the project drawer now that the project has been deleted
      navigate(getPath({ path: Path.ASC_PROJECTS, companyId, fiscalYearId, periodName }));
    } catch (error) {
      const extendedErrors = getExtendedErrorsOrDefault(
        error,
        AscProjectDeletionErrorSchema,
        'Failed to delete project. Please try again.',
      );
      throw new Error(
        `This project cannot be deleted for the following reasons: ${extendedErrors
          .map((error) => error.description)
          .join(', ')}.`,
      );
    }
  };

  return (
    <Stack direction="column">
      <ButtonStyled size="xs" variant="action" rounded="regular" onClick={handleOpenModal}>
        Delete Project
      </ButtonStyled>
      <Modal isOpen={isDeleteProjectModalOpen} setIsOpen={handleCloseModal}>
        <DeleteConfirmationLayout
          title="Delete Project"
          description="Would you like to delete this project? This action cannot be undone."
          close={handleCloseModal}
          deleteMutation={handleConfirmDelete}
          loading={loading}
        />
      </Modal>
    </Stack>
  );
};
